import React from "react";
import {
  FeatherAlertCircle,
  FeatherCheckCircle,
  FeatherXCircle,
} from "@subframe/core";
import { Button } from "../../../ui/components/Button";
import { ReviewRequestDTO } from "../../data/api/reviewsApi";
import { ChangeDetails } from "../../utils/reviewWorkflow";
import {
  formatJournalChangesForBanner,
  formatCreditDebitNoteChanges,
  formatAllLineItemsFromRawJson,
  formatInvoiceBillCreateSummaryFromRawJson,
  formatRecurringJournalFieldsOnly,
  formatJournalHeaderAndLineItemsFromRawJson,
  hasRecurringParamsInRawJson,
} from "./ReviewRequestModal";
import { formatInvoiceBillChangeSummaryLines } from "../../utils/review";
import { useTaxes } from "../hooks/useTaxes";
import type { TaxEntity } from "../../domain/entities/TaxEntity";
import {
  getPushButtonLabel,
  getPushWithoutAttachmentLabel,
  getStoredAccountingBackend,
} from "../../../config/accounting";

interface ReviewStatusBannerProps {
  review: ReviewRequestDTO | null;
  onResubmit?: () => void;
  className?: string;
  moduleCode?: string; // "invoice" or "bill" to determine GST field display
  currentUserId?: number | string | null; // Current user ID to check against review.user_id
}

/**
 * Whether {@link ReviewStatusBanner} will render visible content for this review and user.
 * Pending: visible to everyone. Approved/rejected: only when currentUserId matches review.user_id.
 */
export function isReviewStatusBannerVisibleForUser(
  review: ReviewRequestDTO | null,
  currentUserId: number | string | null | undefined,
): boolean {
  if (!review) return false;
  const { status, user_id } = review;
  if (status === "pending") return true;
  if (status === "approved" || status === "rejected") {
    if (currentUserId == null) return false;
    const reviewUserId =
      typeof user_id === "number" ? user_id : parseInt(String(user_id), 10);
    const currentUserIdNum =
      typeof currentUserId === "number"
        ? currentUserId
        : parseInt(String(currentUserId), 10);
    if (Number.isNaN(reviewUserId) || Number.isNaN(currentUserIdNum))
      return false;
    return reviewUserId === currentUserIdNum;
  }
  return false;
}

/**
 * Format date and time for review display
 * Format: "DD-MM-YYYY, HH:MM AM/PM"
 * Example: "28-01-2026, 1:53 PM"
 */
const formatApprovedDateTime = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString;
    }

    // Format date: "DD-MM-YYYY"
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const formattedDate = `${day}-${month}-${year}`;

    // Format time: "1:53 PM"
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;
    const formattedTime = `${displayHours}:${String(minutes).padStart(
      2,
      "0"
    )} ${ampm}`;

    return `${formattedDate}, ${formattedTime}`;
  } catch {
    return dateString;
  }
};

/**
 * Format change details for display in the review banner
 * Uses the SAME logic as ReviewRequestModal for journals to ensure consistency
 */
const formatChangeDetails = (
  changeDetails: ChangeDetails | undefined,
  moduleCode?: string,
  taxes?: TaxEntity[],
): string[] => {
  if (!changeDetails) return [];

  // Journal draft flow: from saved-as-draft (push, create_recurring, ap_ar_create). Show full journal from raw_json + recurring when present (same as ReviewRequestModal).
  const actionTypes = changeDetails.action_types || [];
  const isJournalDraftFlow =
    (moduleCode?.toLowerCase() === "journal" ||
      moduleCode?.toLowerCase() === "ap_ar_journal") &&
    (actionTypes.includes("push_to_zoho") ||
      actionTypes.includes("push_to_zoho_without_attachment") ||
      actionTypes.includes("create_recurring") ||
      actionTypes.includes("ap_ar_create")) &&
    !actionTypes.includes("update_after_push");
  if (isJournalDraftFlow && changeDetails.raw_json) {
    const journalLines = formatJournalHeaderAndLineItemsFromRawJson(
      changeDetails.raw_json
    );
    const recurringLines = hasRecurringParamsInRawJson(changeDetails.raw_json)
      ? formatRecurringJournalFieldsOnly(changeDetails.raw_json)
      : [];
    return [...journalLines, ...(recurringLines.length > 0 ? ["", ...recurringLines] : [])];
  }

  // For journals with field_changes (e.g. update_after_push), use the exact same formatting logic as ReviewRequestModal
  if (
    (moduleCode?.toLowerCase() === "journal" ||
      moduleCode?.toLowerCase() === "ap_ar_journal") &&
    changeDetails.field_changes
  ) {
    // Use the exported formatJournalChangesForBanner from ReviewRequestModal
    // Pass raw_json and original_json to enable account/contact name lookups
    return formatJournalChangesForBanner(
      changeDetails.field_changes,
      changeDetails.raw_json,
      changeDetails.original_json
    );
  }

  // For credit/debit notes, use the exact same formatting logic as ReviewRequestModal
  if (
    (moduleCode?.toLowerCase() === "credit_note" ||
      moduleCode?.toLowerCase() === "debit_note") &&
    changeDetails.field_changes
  ) {
    // Use the exported formatCreditDebitNoteChanges from ReviewRequestModal
    return formatCreditDebitNoteChanges(changeDetails.field_changes);
  }

  // Create action for manual invoice/bill: use structured summary (same as ReviewRequestModal/ApprovalDetailPanel)
  const isCreateInvoiceOrBill =
    (moduleCode?.toLowerCase() === "invoice" ||
      moduleCode?.toLowerCase() === "bill") &&
    actionTypes.includes("create");
  if (isCreateInvoiceOrBill && changeDetails.raw_json) {
    const createSummary = formatInvoiceBillCreateSummaryFromRawJson(
      changeDetails.raw_json,
      moduleCode?.toLowerCase() || ""
    );
    if (createSummary.length > 0) return createSummary;
  }

  // Delete after push to Zoho (invoice/bill): show full document details so user sees what will be deleted (no "changes summary")
  const isDeleteInvoiceOrBill =
    (moduleCode?.toLowerCase() === "invoice" ||
      moduleCode?.toLowerCase() === "bill") &&
    actionTypes.includes("delete_after_push_to_zoho");
  if (isDeleteInvoiceOrBill && changeDetails.raw_json) {
    const deleteSummary = formatInvoiceBillCreateSummaryFromRawJson(
      changeDetails.raw_json,
      moduleCode?.toLowerCase() || ""
    );
    if (deleteSummary.length > 0) return deleteSummary;
  }

  if (
    moduleCode?.toLowerCase() === "invoice" ||
    moduleCode?.toLowerCase() === "bill"
  ) {
    const kind =
      moduleCode?.toLowerCase() === "invoice" ? "invoice" : "bill";
    const lines = formatInvoiceBillChangeSummaryLines(
      kind,
      changeDetails,
      taxes,
    );
    const hasFieldChanges = (changeDetails.field_changes?.length ?? 0) > 0;
    const hasSnapshot =
      changeDetails.original_json != null && changeDetails.raw_json != null;
    const skipFullLineItems = hasFieldChanges || hasSnapshot;
    const lineItemsSection = skipFullLineItems
      ? []
      : formatAllLineItemsFromRawJson(changeDetails.raw_json, moduleCode);
    return [...lines, ...lineItemsSection];
  }

  return [];
};

/**
 * ReviewStatusBanner Component
 *
 * Displays review status with appropriate styling:
 * - Pending: Warning banner (yellow/orange), blocks all actions
 * - Rejected: Error banner (red), shows rejection reason + "Resubmit for Review" button
 * - Approved: Info banner (blue/green), optional - mainly shown in modal
 */
export const ReviewStatusBanner: React.FC<ReviewStatusBannerProps> = ({
  review,
  onResubmit,
  className = "",
  moduleCode,
  currentUserId,
}) => {
  const { taxes } = useTaxes();
  if (!review || !isReviewStatusBannerVisibleForUser(review, currentUserId)) {
    return null;
  }

  const { status, request_comments, review_comments, reviewed_at } = review;

  if (status === "pending") {
    // Check if this is a reject or duplicate action
    const isRejectAction = review.action_code === "reject";
    const isDuplicateAction = review.action_code === "duplicate";
    const actionName = review.action_name || review.action_code;

    // Determine the entity type for custom messaging
    const entityType = review.entity_type || "";
    const isJournalEntity =
      entityType === "journal" || entityType === "ap_ar_journal";
    const entityName =
      entityType === "invoice"
        ? "Invoice"
        : entityType === "bill"
        ? "Bill"
        : entityType === "ap_ar_journal"
        ? "AP/AR Journal"
        : entityType;

    // Build the pending reason message
    const journalActionTypes = review.change_details?.action_types || [];
    const isCreateRecurringOnly =
      isJournalEntity &&
      journalActionTypes.includes("create_recurring") &&
      !journalActionTypes.includes("push_to_zoho") &&
      !journalActionTypes.includes("push_to_zoho_without_attachment") &&
      !journalActionTypes.includes("update_after_push") &&
      !journalActionTypes.includes("delete_after_push");
    const hasPushAndCreateRecurring =
      isJournalEntity &&
      journalActionTypes.includes("create_recurring") &&
      (journalActionTypes.includes("push_to_zoho") ||
        journalActionTypes.includes("push_to_zoho_without_attachment"));
    const backend = getStoredAccountingBackend();
    const journalActionLabels: Record<string, string> = {
      push_to_zoho: getPushButtonLabel(backend),
      push_to_zoho_without_attachment: getPushWithoutAttachmentLabel(backend),
      create_recurring: "Create Recurring",
      ap_ar_create: "AP/AR Journal – Create",
      ap_ar_delete: "AP/AR Journal – Delete",
    };
    const displayActionName =
      journalActionLabels[review.action_code] || actionName;
    let pendingReasonMessage = "";
    if (isRejectAction) {
      pendingReasonMessage = `Pending review for: Reject`;
    } else if (isDuplicateAction) {
      pendingReasonMessage = `Pending review for: Mark as Duplicate`;
    } else if (hasPushAndCreateRecurring) {
      const parts = journalActionTypes
        .filter(
          (a: string) =>
            a === "push_to_zoho" ||
            a === "push_to_zoho_without_attachment" ||
            a === "create_recurring"
        )
        .map((a: string) => journalActionLabels[a] || a);
      pendingReasonMessage = `Pending review for: ${parts.join(", ")}`;
    } else if (isCreateRecurringOnly) {
      pendingReasonMessage = `Request to create this journal as recurring`;
    } else if (displayActionName && displayActionName !== "Update" && displayActionName !== "Edit") {
      pendingReasonMessage = `Pending review for: ${displayActionName}`;
    }

    return (
      <div
        className={`flex w-full flex-col items-start gap-3 rounded-md border border-solid border-warning-200 bg-warning-50 px-4 py-4 ${className}`}
        role="alert"
      >
        <div className="flex w-full items-center gap-2">
          <FeatherAlertCircle className="h-5 w-5 text-warning-600 flex-none" />
          <span className="text-body-bold font-body-bold text-warning-700">
            Review Pending
          </span>
        </div>
        {(review.change_details?.closed_month === true ||
          review.change_details?.action_types?.includes("month_close")) && (
          <div className="flex flex-col items-start gap-1 pl-7">
            <span className="text-body font-body text-warning-700">
              Post-close change — this request is for a change in a closed
              month.
            </span>
          </div>
        )}
        {pendingReasonMessage && (
          <div className="flex flex-col items-start gap-1 pl-7">
            <span className="text-body-bold font-body-bold text-warning-700">
              {pendingReasonMessage}
            </span>
          </div>
        )}
        {request_comments && (
          <div className="flex flex-col items-start gap-1 pl-7">
            <span className="text-body font-body text-warning-700">
              <strong>Request Comments:</strong> {request_comments}
            </span>
          </div>
        )}
        {(() => {
          // For pending create invoice/bill: do NOT show "Changes Requested"
          const isCreateInvoiceOrBill =
            (entityType === "invoice" || entityType === "bill") &&
            review.change_details?.action_types?.includes("create");
          if (isCreateInvoiceOrBill) {
            return null;
          }
          const formattedChanges = formatChangeDetails(
            review.change_details,
            moduleCode,
            taxes,
          );
          const isDeleteAction = review.change_details?.action_types?.includes(
            "delete_after_push_to_zoho"
          );
          // For delete after push to zoho: do not show Document details in the banner (invoice/bill review pages)
          if (isDeleteAction) return null;
          return formattedChanges.length > 0 ? (
            <div className="flex w-full flex-col items-start gap-2 pl-7">
              <span className="text-body-bold font-body-bold text-warning-700">
                Changes Requested:
              </span>
              <div className="flex w-full flex-col items-start gap-1 rounded-md bg-warning-100 px-3 py-2 border border-solid border-warning-300">
                <div className="text-body font-body text-warning-800 whitespace-pre-line leading-relaxed">
                  {formattedChanges.join("\n")}
                </div>
              </div>
            </div>
          ) : null;
        })()}
        <div className="flex flex-col items-start gap-1 pl-7">
          <span className="text-body font-body text-warning-700">
            This document is pending review. All actions are blocked until the
            review is completed.
          </span>
        </div>
      </div>
    );
  }

  if (status === "rejected") {
    return (
      <div
        className={`flex w-full flex-col items-start gap-3 rounded-md border border-solid border-error-200 bg-error-50 px-4 py-4 ${className}`}
        role="alert"
      >
        <div className="flex w-full items-center gap-2">
          <FeatherXCircle className="h-5 w-5 text-error-600 flex-none" />
          <span className="text-body-bold font-body-bold text-error-700">
            Review Rejected
          </span>
        </div>
        {review_comments && (
          <div className="flex flex-col items-start gap-1 pl-7">
            <span className="text-body font-body text-error-700">
              <strong>Rejection Reason:</strong> {review_comments}
            </span>
          </div>
        )}
        {(() => {
          const formattedChanges = formatChangeDetails(
            review.change_details,
            moduleCode,
            taxes,
          );
          const isDeleteAction = review.change_details?.action_types?.includes(
            "delete_after_push_to_zoho"
          );
          // For delete after push to zoho: do not show Document details in the banner (invoice/bill review pages)
          if (isDeleteAction) return null;
          return formattedChanges.length > 0 ? (
            <div className="flex w-full flex-col items-start gap-2 pl-7">
              <span className="text-body-bold font-body-bold text-error-700">
                Changes Requested:
              </span>
              <div className="flex w-full flex-col items-start gap-1 rounded-md bg-error-100 px-3 py-2 border border-solid border-error-300">
                <div className="text-body font-body text-error-800 whitespace-pre-line leading-relaxed">
                  {formattedChanges.join("\n")}
                </div>
              </div>
            </div>
          ) : null;
        })()}
        {reviewed_at && (
          <div className="flex flex-col items-start gap-1 pl-7">
            <span className="text-body font-body text-error-700">
              <strong>Rejected on:</strong>{" "}
              {formatApprovedDateTime(reviewed_at)}
            </span>
          </div>
        )}
        {/* {onResubmit && (
          <div className="flex w-full items-center justify-end pt-2">
            <Button variant="brand-primary" onClick={onResubmit}>
              Resubmit for Review
            </Button>
          </div>
        )} */}
      </div>
    );
  }

  if (status === "approved") {
    return (
      <div
        className={`flex w-full flex-col items-start gap-3 rounded-md border border-solid border-success-200 bg-success-50 px-4 py-4 ${className}`}
        role="alert"
      >
        <div className="flex w-full items-center gap-2">
          <FeatherCheckCircle className="h-5 w-5 text-success-600 flex-none" />
          <span className="text-body-bold font-body-bold text-success-700">
            Review Approved
          </span>
        </div>
        {review_comments && (
          <div className="flex flex-col items-start gap-1 pl-7">
            <span className="text-body font-body text-success-700">
              <strong>Review Comments:</strong> {review_comments}
            </span>
          </div>
        )}
        {(() => {
          const formattedChanges = formatChangeDetails(
            review.change_details,
            moduleCode,
            taxes,
          );
          const isDeleteAction = review.change_details?.action_types?.includes(
            "delete_after_push_to_zoho"
          );
          // For delete after push to zoho: do not show Document details in the banner (invoice/bill review pages)
          if (isDeleteAction) return null;
          return formattedChanges.length > 0 ? (
            <div className="flex w-full flex-col items-start gap-2 pl-7">
              <span className="text-body-bold font-body-bold text-success-700">
                Approved Changes:
              </span>
              <div className="flex w-full flex-col items-start gap-1 rounded-md bg-success-100 px-3 py-2 border border-solid border-success-300">
                <div className="text-body font-body text-success-800 whitespace-pre-line leading-relaxed">
                  {formattedChanges.join("\n")}
                </div>
              </div>
            </div>
          ) : null;
        })()}
        {reviewed_at && (
          <div className="flex flex-col items-start gap-1 pl-7">
            <span className="text-body font-body text-success-700">
              <strong>Approved on:</strong>{" "}
              {formatApprovedDateTime(reviewed_at)}
            </span>
          </div>
        )}
      </div>
    );
  }

  return null;
};
